import React from "react";
import ReactDOM from "react-dom";
import { graphql, useFragment } from "react-relay";
import { useHistory, useRouteMatch } from "react-router-dom";

import { compact } from "lodash";
import moment from "moment";

import Attachments, { AttachmentsPlugin, AttachmentsRelay } from "~/attachments/AttachmentsRelay";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { getAllMarks } from "~/lib/prose/marks";
import {
	BulletPoint,
	CustomEmoji,
	Embed,
	HardBreak,
	HorizontalRule,
	KeyResult,
	Mention,
	NumberedListItem,
	Tag,
	TextNode,
	Unstyled,
} from "~/lib/prose/nodes";
import { Header as HeaderNode } from "~/lib/prose/nodes/header";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import {
	Agenda,
	BlockCreator,
	Collab,
	DropTarget,
	ExternalAttribute,
	Indent,
	InputRules,
	MentionProvider,
	ModS,
	MoveSelection,
	MultiCursor,
	MultiSelect,
	MultiSelectDropHandler,
	Paste,
	Placeholder,
	SlashProvider,
	Todo,
	TypeAhead,
} from "~/lib/prose/plugins";
import ActionItemIntegrationsPlugin, {
	proseIntegrationKey as actionItemIntegrationLinkProseKey,
} from "~/lib/prose/plugins/actionItemIntegrationLink";
import ActionItemProgressPlugin from "~/lib/prose/plugins/actionItemProgress";
import { AITopicsExtension } from "~/lib/prose/plugins/aiTopics";
import AsanaItems, { type AsanaItemsUpdateData } from "~/lib/prose/plugins/asanaItems";
import { BlockActions } from "~/lib/prose/plugins/blockActions";
import BlockAnnotation from "~/lib/prose/plugins/blockAnnotation";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import type { StatusChange } from "~/lib/prose/plugins/collab";
import { CollapseExtension } from "~/lib/prose/plugins/collapse";
import Comments from "~/lib/prose/plugins/comments";
import {
	FellowAttachmentHandler,
	FileDropHandler,
	ImageStripItemHandler,
	NoteTaskDropHandler,
} from "~/lib/prose/plugins/dropTarget";
import EmojiProvider, { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import FilePaste from "~/lib/prose/plugins/filePaste";
import FocusArea from "~/lib/prose/plugins/focusArea";
import { Keyboard } from "~/lib/prose/plugins/keyboard";
import MousePos from "~/lib/prose/plugins/mousePos";
import type { UserPresence } from "~/lib/prose/plugins/multicursor";
import { PromptExtension } from "~/lib/prose/plugins/prompt";
import Reactions from "~/lib/prose/plugins/reactions";
import RecordVideo from "~/lib/prose/plugins/recordVideo/recordVideo";
import SelectedBlock from "~/lib/prose/plugins/selectedBlock";
import TagProvider from "~/lib/prose/plugins/tag";
import Timestamp, { withTimestampAttrs } from "~/lib/prose/plugins/timestamp/timestamp";
import { FloatingFormattingTools } from "~/lib/prose/plugins/toolbar/floatingToolbarExtension";
import PinnedFormattingTools from "~/lib/prose/plugins/toolbar/pinnedToolbarExtension";
import { decodeGlobalId } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { fromEntries, isTouchScreen } from "~/lib/utils";
import WebsocketClient from "~/lib/websocket";
import { threadKeyFromNoteIdAndBlockKey, toPathForTab } from "~/meetings/SeriesSidePanel/Comments/threadKeys";
import { eventEmitter } from "~/stream/Comments/events";
import { StreamSideBarContext } from "~/stream/SideBar/SideBarContext";
import { baseStreamUrl } from "~/stream/utils";

import AppContext from "../common/AppContext";
import type { NoteSocketPayload } from "../stream/NoteCard";
import { NoteLocked, NoteReadOnly } from "../stream/StreamSettings/Automations/Banners";
import { formatLastUpdatedDate } from "../stream/utils";
import { useAttendeePresenceContext } from "./AttendeePresenceContext";
import { useNoteSaveState } from "./NoteSaveStateContext";

import type { MeetingNote_note$key } from "~/__generated__/MeetingNote_note.graphql";
import type { MeetingNote_viewer$key } from "~/__generated__/MeetingNote_viewer.graphql";

import styles from "./MeetingNote.module.css";

const NARROW_WIDTH_BREAKPOINT = 734;

type MeetingNoteProps = {
	note: MeetingNote_note$key;
	viewer: MeetingNote_viewer$key;
	onTimestampClick: (timestamp: number, recordingId?: string) => void;
};

export function MeetingNote({ note: noteKey, viewer: viewerKey, onTimestampClick }: MeetingNoteProps) {
	const note = useFragment(
		graphql`
			fragment MeetingNote_note on Note {
				id
				title
				url
				eventGuid
				rawDraftJson
				updatedAt
				createdAt
				eventStart
				eventEnd
				finished
				collapsedBlockKeys
				isLocked
				canUnlockNote
				groupName
				viewerCanEdit
				comments {
					blockKey
					count
					users {
						id
						fullName
						image
						isExternal
					}
				}
				reactions
				attachments {
					id
					dimensions {
						height
						width
					}
					...AttachmentPreviewAttachment
				}
				noteuserSet {
					id
					lastSeenAt
					user {
						__typename
						id
						type
						email
						firstName
						fullName
						image
						isActive
						isConfigured
						isGuest
						isTopUser
						isExternal
						internalAttributes {
							invitable
						}
					}
				}
				externalUsers {
					__typename
					id
					type
					email
					firstName
					fullName
					image
					isActive
					isConfigured
					isGuest
					isTopUser
					isExternal

					internalAttributes {
						invitable
					}
				}
				linkedActionItems: actionItems(linkedOnly: true) {
					hasOriginalBlock
					originalBlockId
					originalBlock {
						key
					}
				}

				...asanaItems @relay(mask: false)
				...actionItemIntegrationLink @relay(mask: false)

				stream {
					id
					type
					isExternal
					account {
						id
					}
				}
				...AttachmentsRelay_note
			}
		`,
		noteKey,
	);

	const viewer = useFragment(
		graphql`
			fragment MeetingNote_viewer on Viewer {
				user {
					id
					isGuest
					firstName
				}
				account {
					loomEnabled
					tags {
						__typename
						id
						label
						color
					}
					billingAccount {
						hasAccessToDuedates: hasAccessToFeature(feature: duedates)
						hasAccessToVersioning: hasAccessToFeature(feature: versioning)
						hasAccessToSharing: hasAccessToFeature(feature: sharing)
						hasAccessToJiraIntegration: hasAccessToFeature(feature: jira_integration)
						hasAccessToComments: hasAccessToFeature(feature: comments)
						hasAccessToMeetingFeedback: hasAccessToFeature(feature: meeting_feedback)
						hasAccessToNoteLocking: hasAccessToFeature(feature: note_locking)
						hasAccessToZapierIntegration: hasAccessToFeature(feature: zapier_integration)
						hasAccessToEmbeds: hasAccessToFeature(feature: embeds)
						hasAccessToSalesforce: hasAccessToFeature(feature: salesforce_integration)
						hasAccessToHubspot: hasAccessToFeature(feature: hubspot_integration)
						hasAccessToAsana: hasAccessToFeature(feature: asana_integration)
						hasAccessToObjectives: hasAccessToFeature(feature: objectives)
						hasAccessToPromptHeadings: hasAccessToFeature(feature: prompt_headings)
						hasAccessToNotesIntegrationExport: hasAccessToFeature(feature: notes_integration_export)
						hasAccessToMultipleAssignees: hasAccessToFeature(feature: multiple_assignees)
						featureAccess
					}
					integrations {
						jira {
							id
							clientKey
							baseUrl
						}
					}
				}
				connectedActionItemIntegrations
				integrations {
					asana {
						isConnected
					}
				}
			}
		`,
		viewerKey,
	);

	const history = useHistory();
	const match = useRouteMatch();
	const { setCommentContext } = React.useContext(StreamSideBarContext);

	const editorRef = React.useRef<ProseReact>(null);

	const [attachmentsContainer, setAttachmentsContainer] = React.useState<HTMLElement>();
	const [attachmentDropzoneRef, setAttachmentDropzoneRef] = React.useState<AttachmentsRelay>();
	const collabLastSaved = React.useRef<number>(0);

	const { user } = React.useContext(AppContext);

	const updateGroupMetadata = React.useCallback(
		(override: boolean) => {
			const groupName = note.groupName;
			if (!groupName) return;
			WebsocketClient.getInstance().updateGroupMetadata(groupName, override, {
				lastEntityId: Date.now().toString(),
			});
		},
		[note.groupName],
	);

	const onSocketMessage = React.useCallback(
		(payload: NoteSocketPayload) => {
			if (payload.type === "REACTIONS_CHANGED") {
				const reactionsExt = editorRef.current?.editorView?.extensions.get("block-reactions");
				if (reactionsExt instanceof Reactions) {
					reactionsExt.dispatchReactionUpdate(payload.data, true);
				}
			} else if (payload.type === "COMMENTS_CHANGED") {
				updateGroupMetadata(true);
				const commentsExt = editorRef.current?.editorView?.extensions.get("block-comments");
				if (commentsExt instanceof Comments) {
					commentsExt.dispatchCommentsUpdate(payload.data);
				}
			} else if (
				payload.type === "ASANA_UPDATED" ||
				payload.type === "ASANA_COMPLETED" ||
				payload.type === "ASANA_ITEM_UNLINKED"
			) {
				const { type, data } = payload;
				const asanaUpdateData: AsanaItemsUpdateData = { ...data, updateType: type };
				const asanaItemsExt = editorRef.current?.editorView?.extensions.get("asana-items");
				if (asanaItemsExt instanceof AsanaItems) {
					asanaItemsExt.dispatchAsanaUpdate(asanaUpdateData).catch(console.error);
				}
			} else if (payload.type === "ACTION_ITEM_INTEGRATION_LINK_UPDATED") {
				const { data } = payload;
				const actionItemIntegrationPlugin = editorRef.current?.editorView?.extensions.get(
					actionItemIntegrationLinkProseKey,
				);
				if (actionItemIntegrationPlugin instanceof ActionItemIntegrationsPlugin) {
					actionItemIntegrationPlugin.dispatchActionItemIntegrationLinkUpdate(data).catch(console.error);
				}
			}
		},
		[updateGroupMetadata],
	);

	const onShowCommentsMessage = React.useCallback(
		(noteId: string, blockKey: string, _options: { intendToAdd?: boolean } = {}) => {
			if (noteId !== note.id) return;
			if (window.innerWidth <= NARROW_WIDTH_BREAKPOINT) {
				history.push(
					`${baseStreamUrl(match.params, user?.isGuest ?? false)}comments/${toPathForTab({
						noteId,
						blockKey,
					})}`,
				);
			}
			const threadKey = threadKeyFromNoteIdAndBlockKey(noteId, blockKey);
			setCommentContext({ threadKey, noteId, blockId: blockKey });
		},
		[history, match.params, note.id, setCommentContext, user?.isGuest],
	);

	React.useEffect(() => {
		if (!note.groupName) return;
		const socket = WebsocketClient.getInstance().getOrJoinGroup(note.groupName);
		socket.on("message", onSocketMessage);

		return () => {
			socket.off("message", onSocketMessage);
			socket.close();
		};
	}, [note.groupName, onSocketMessage]);

	React.useEffect(() => {
		eventEmitter.on("NoteComments.open", onShowCommentsMessage);
		return () => {
			eventEmitter.off("NoteComments.open", onShowCommentsMessage);
		};
	}, [onShowCommentsMessage]);

	const { onUserPresenceChange } = useAttendeePresenceContext();
	const onUserPresenceChangeWithAnonymous = React.useCallback(
		(presence: UserPresence) => {
			const { user } = presence;
			const noteUser = note.noteuserSet.find(nu => nu.user.email === user.email);
			const isNewAnonymousUser = Boolean(!noteUser && user.isAnonymous);
			onUserPresenceChange(presence, isNewAnonymousUser);
		},
		[note.noteuserSet, onUserPresenceChange],
	);

	const onUploadFiles = React.useCallback(
		(files: File[]) => {
			dispatchAnalyticsTrack("Meeting notes paste file", { feature: "meeting-notes" });
			attachmentDropzoneRef?.upload(files);
		},
		[attachmentDropzoneRef],
	);

	const { updateNoteSaveStatus } = useNoteSaveState();
	const onEditorStatusChange = React.useCallback(
		(e: CustomEvent<StatusChange>) => {
			collabLastSaved.current = e.detail.lastSaveTime;
			updateNoteSaveStatus(e.detail.online ? (e.detail.saved ? "saved" : "saving") : "offline");
		},
		[updateNoteSaveStatus],
	);

	const collab = React.useMemo(() => {
		const newCollab = new Collab({
			documentId: note.id,
			sessionId: window.CLIENT_SESSION_ID,
			userId: note.viewerCanEdit ? decodeGlobalId(user?.id ?? "")[1] : -1,
			initialData: note.rawDraftJson,
		});
		newCollab.addEventListener("statuschange", onEditorStatusChange as EventListener);
		return newCollab;
	}, [user, note, onEditorStatusChange]);

	React.useEffect(() => {
		return () => {
			if (!collab) return;
			collab.removeEventListener("statuschange", onEditorStatusChange as EventListener);
			updateNoteSaveStatus("unknown");
		};
	}, [collab, onEditorStatusChange, updateNoteSaveStatus]);

	const externalAttribute = React.useMemo(() => {
		return new ExternalAttribute({
			documentId: note.id,
			sessionId: window.CLIENT_SESSION_ID,
			initialData: note.rawDraftJson,
		});
	}, [note]);

	const multicursor = React.useMemo(() => {
		return new MultiCursor({
			documentId: note.id,
			sessionId: window.CLIENT_SESSION_ID,
			user: note.viewerCanEdit ? viewer.user : null,
			collab: collab,
			onUserPresenceChange: onUserPresenceChangeWithAnonymous,
		});
	}, [note.id, note.viewerCanEdit, viewer.user, collab, onUserPresenceChangeWithAnonymous]);

	// NOTE: This was far too easy to miss, it would be nice if this was just handled in the multicursor plugin -Wal
	React.useEffect(() => {
		multicursor.onScreenVisible();
		return () => {
			multicursor.onScreenHidden();
		};
	}, [multicursor]);

	const editorConfig = React.useMemo(() => {
		const markOptions = note.viewerCanEdit
			? { linkEditable: true, placeholderEditable: false, linkToolBarAllowEmbeds: true }
			: {
					linkEditable: false,
					placeholderEditable: false,
					linkToolBarAllowEmbeds: false,
					disableLinkToolbar: true,
					disablePlaceholderToolbar: true,
				};

		const readOnlyOptions = note.viewerCanEdit
			? {
					disabledReadOnly: false,
				}
			: {
					disabledReadOnly: true,
					readOnly: true,
				};

		const extensions = [
			...getAllMarks(markOptions),
			new BlockActions(),
			new BlockBoundary(),
			new CollapseExtension(note.collapsedBlockKeys),
			new Keyboard(),
			new TextNode(),
			new Indent(),
			new Unstyled(),
			new BulletPoint(),
			new NumberedListItem(),
			new ImageStrip({
				contentId: note.id,
				disableWhenNotEditable: true,
				hidePreview: false,
				initialDimensions: note.attachments.filter(Boolean).reduce(
					(acc: Record<string, { width: number | undefined; height: number | undefined }>, attachment) => ({
						...acc,
						[attachment.id]: attachment.dimensions
							? {
									width: attachment.dimensions.width,
									height: attachment.dimensions.height,
								}
							: { width: undefined, height: undefined },
					}),
					{},
				),
			}),
			new ImageStripItem(),
			new ImageStripItemFile(),
			new ImageStripItemCaption(),
			new HardBreak(),
			new HorizontalRule(),
			new HeaderNode({ viewer: viewer }),
			new AITopicsExtension({ noteId: note.id }),
			new Mention({ currentUserId: viewer.user.id }),
			new Tag({
				showToolbar: !viewer.user.isGuest,
				allowEditing: !viewer.user.isGuest,
				clickable: !viewer.user.isGuest,
			}),
			new CustomEmoji({ accountId: note.stream.account.id }),
			new Placeholder(),
			new DropTarget({
				handlers: [
					new NoteTaskDropHandler(),
					new MultiSelectDropHandler(),
					new FileDropHandler(onUploadFiles),
					new FellowAttachmentHandler(),
					new ImageStripItemHandler(),
				],
			}),
			...(note.viewerCanEdit ? [new MultiSelect()] : []),
			new MousePos(),
			new MoveSelection(),
			new ModS(),
			new FilePaste({ upload: onUploadFiles }),
			// TODO: …what is AttachmentsPlugin responsible for? -Wal
			new AttachmentsPlugin({ setAttachmentsRef: setAttachmentsContainer }),
			new BlockCreator({ userId: viewer.user.id }),
			new FocusArea({ getTarget: (dom: HTMLElement) => dom.closest(".notes-stream-card")?.querySelector(".main") }),
			new (withTimestampAttrs(Todo))({
				note: note,
				enableCopiedItemPopup: true,
				disableWhenNotEditable: true,
				viewerId: viewer.user.id,
				...readOnlyOptions,
			}),
			new Timestamp({ onTimestampClick: onTimestampClick }),
			new Agenda({
				note: note,
				enableCopiedItemPopup: true,
				employeeGuidanceShowManager: false,
				viewerId: viewer.user.id,
				...readOnlyOptions,
			}),
			new TypeAhead({
				providers: [
					new SlashProvider({
						isExternalUser: note.stream.isExternal,
					}),
					new MentionProvider(),
					new EmojiProvider(),
					new TagProvider({ viewerIsGuest: viewer.user.isGuest }),
				],
			}),
			new ActionItemIntegrationsPlugin({
				noteId: note.id,
				initialData: note.integrationLinkedActionItems,
			}),
			new ActionItemProgressPlugin(),
			new Reactions({
				user: {
					id: viewer.user.id,
					firstName: viewer.user.firstName,
				},
				account: note.stream.account,
				noteId: note.id,
				initialData: JSON.parse(note.reactions),
			}),
			isTouchScreen ? new PinnedFormattingTools() : new FloatingFormattingTools(),
			new Comments({
				noteId: note.id,
				initialData: note.comments,
			}),
			new AsanaItems({
				noteId: note.id,
				initialData: note.asanaActionItems,
			}),
			new Paste(),
			new PasteBuiltInEmoji(),
			new SelectedBlock(),
			new InputRules(),
			new Embed({ renderFrame: true }),
			new KeyResult(),
			multicursor,
			collab,
			externalAttribute,
			new BlockAnnotation(),
			...(viewer.account.loomEnabled ? [new RecordVideo()] : []),
			new PromptExtension(),
		];

		return new EditorConfig({
			extensions: extensions,
			nodes: {
				doc: {
					content: "block+",
				},
			},
		});
	}, [note, viewer, onUploadFiles, multicursor, collab, externalAttribute, onTimestampClick]);

	const getLastUpdatedString = React.useCallback(() => {
		const lastUpdatedDate = moment(collabLastSaved.current || note.updatedAt || "");
		return formatLastUpdatedDate(lastUpdatedDate);
	}, [note]);

	const proseContext = React.useMemo(() => {
		const noteUsers = (note.noteuserSet || []).filter(Boolean).map(nu => ({ ...nu, ...nu.user, invitable: false }));
		const externalUsers = (note.externalUsers || []).map(eu => ({ ...eu, user: eu, invitable: false }));
		const participants = [...noteUsers, ...externalUsers];
		return {
			participants,
			getLastUpdatedString,
			noteId: note.id,
			noteUrl: note.url,
			noteEventGuid: note.eventGuid,
			streamId: note.stream.id,
			streamType: note.stream.type,
			hasAccessToPromptHeadings: viewer.account.billingAccount.hasAccessToPromptHeadings,
			billing: viewer.account.billingAccount,
			locked: note.isLocked,
			userId: viewer.user.id,
			viewOnlyAccess: !note.viewerCanEdit,
			hasAccessToOriginalBlock: fromEntries(
				compact(
					note.linkedActionItems
						.filter(item => item.originalBlock)
						.map(
							item =>
								item.hasOriginalBlock && item.originalBlockId && [item.originalBlockId, item.originalBlock != null],
						),
				) as [string | number | symbol, unknown][],
			),
			asanaIsConnected: viewer.integrations.asana?.isConnected,
			connectedActionItemIntegrations: viewer.connectedActionItemIntegrations,
			jira: viewer.account.integrations.jira,
			tags: viewer.account.tags,
		};
	}, [note, viewer, getLastUpdatedString]);

	return (
		<>
			{note.viewerCanEdit ? (
				note.isLocked ? (
					<div className={styles.noteNoticeContainer}>
						<NoteLocked canUnlockNote={note.canUnlockNote} />
					</div>
				) : null
			) : (
				<div className={styles.noteNoticeContainer}>
					<NoteReadOnly />
				</div>
			)}

			<div className={styles.noteWrapper} data-tour-tooltip-target="agenda-editor">
				{note.rawDraftJson && (
					<ProseReact
						key={`meeting-note-${note.id}-${note.eventGuid || "no-event"}`}
						className={styles.editor}
						ref={editorRef}
						enableReactHost
						editable={!note.isLocked && note.viewerCanEdit}
						config={editorConfig}
						lockedNote={note.isLocked || !note.viewerCanEdit}
						initialDoc={note.rawDraftJson.doc}
						context={proseContext}
					/>
				)}
				{attachmentsContainer &&
					ReactDOM.createPortal(
						<Attachments componentRef={setAttachmentDropzoneRef} groupId={note.id} note={note} />,
						attachmentsContainer,
					)}
			</div>
		</>
	);
}
